import { ApolloError } from "@apollo/client";
import { Add as IconAdd, Save as IconSave } from "@mui/icons-material";
import { AccordionDetails, Button, Paper, Stack } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Shared_FormTemplate_QuestionFragment } from "~/shared/components/FormTemplate/fragments/fragments.generated";

import isFormTemplateQuestionDropdownOrSelect from "../../util/isFormTemplateQuestionDropdownOrSelect";
import FormTemplateQuestionForm, { FormTemplateQuestionFormInputs } from "../FormTemplateQuestionForm";
import FormTemplateQuestionItem from "../FormTemplateQuestionItem";

type Props = {
    formTemplateQuestions: Shared_FormTemplate_QuestionFragment[];
    onAddQuestion: (data: FormTemplateQuestionFormInputs) => Promise<void>;
    onUpdateQuestion: (formTemplateQuestionId: string, data: FormTemplateQuestionFormInputs) => Promise<void>;
    onDeleteQuestion: (id: string) => Promise<void>;
    isAddQuestionLoading: boolean;
    isUpdateQuestionLoading: boolean;
    isDeleteQuestionLoading: boolean;
    addQuestionError?: ApolloError;
    editQuestionError?: ApolloError;
};

export default function FormTemplateEditor({
    formTemplateQuestions,
    onAddQuestion,
    onUpdateQuestion,
    onDeleteQuestion,
    isAddQuestionLoading,
    isUpdateQuestionLoading,
    isDeleteQuestionLoading,
    addQuestionError,
    editQuestionError,
}: Props) {
    const { t } = useTranslation("formTemplateEditor");
    const [showQuestionCreationForm, setShowQuestionCreationForm] = useState<boolean>(false);

    const handleAddQuestion = async (data: FormTemplateQuestionFormInputs) => {
        await onAddQuestion(data);
        setShowQuestionCreationForm(false);
    };

    const selectableConditionQuestions = formTemplateQuestions.filter(q =>
        isFormTemplateQuestionDropdownOrSelect(q.type.__typename),
    );

    return (
        <Stack direction="column" rowGap={2}>
            {formTemplateQuestions.map(formTemplateQuestion => (
                <FormTemplateQuestionItem
                    selectableConditionQuestions={selectableConditionQuestions}
                    currentQuestion={formTemplateQuestion}
                    key={formTemplateQuestion.id}
                    onUpdate={onUpdateQuestion}
                    onDelete={onDeleteQuestion}
                    isUpdateQuestionLoading={isUpdateQuestionLoading}
                    isDeleteQuestionLoading={isDeleteQuestionLoading}
                    error={editQuestionError}
                />
            ))}

            {showQuestionCreationForm ? (
                <Paper>
                    <AccordionDetails>
                        <FormTemplateQuestionForm
                            selectableConditionQuestions={selectableConditionQuestions}
                            onSubmit={handleAddQuestion}
                            actions={(isDirtyState, canSave) => (
                                <Stack direction="row" justifyContent="space-between">
                                    <Button onClick={() => setShowQuestionCreationForm(false)} color="primary">
                                        {t("formTemplateQuestionForm.btnCancel")}
                                    </Button>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        variant="contained"
                                        disabled={isDirtyState || !canSave}
                                        loading={isAddQuestionLoading}
                                    >
                                        <IconSave />
                                        {t("formTemplateQuestionForm.btnSave")}
                                    </Button>
                                </Stack>
                            )}
                            error={addQuestionError}
                        />
                    </AccordionDetails>
                </Paper>
            ) : (
                <Button
                    onClick={() => setShowQuestionCreationForm(true)}
                    startIcon={<IconAdd />}
                    fullWidth
                    sx={{ borderStyle: "dotted", borderColor: "inherit", borderWidth: 2 }}
                    variant="outlined"
                >
                    {t("formTemplateEditor.btnAddField")}
                </Button>
            )}
        </Stack>
    );
}
